// QuestionMultiple - Pregunta tipo Opción Múltiple (una sola opción)
// Sprint 3 - Mobile-first design

import React, { useState } from 'react';
import {
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Chip,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { PreguntaCompleta } from '../../../../types/formulario.types';
import { QuestionResponse } from '../../../../types/audit.types';

interface QuestionMultipleProps {
  pregunta: PreguntaCompleta;
  respuesta?: QuestionResponse;
  onChange: (valor: any, comentario?: string) => void;
}

const QuestionMultiple: React.FC<QuestionMultipleProps> = ({
  pregunta,
  respuesta,
  onChange,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const valorActual = (respuesta as any)?.response_value as { opcion?: string } | undefined;
  const [comentario, setComentario] = useState((respuesta as any)?.comment || '');

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const opcion = event.target.value;
    onChange({ opcion }, comentario || undefined);
  };

  const handleToggleChange = (_event: React.MouseEvent<HTMLElement>, newValue: string | null) => {
    if (newValue) {
      onChange({ opcion: newValue }, comentario || undefined);
    }
  };

  const handleComentarioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nuevoComentario = event.target.value;
    setComentario(nuevoComentario);
    if (valorActual?.opcion) {
      onChange({ opcion: valorActual.opcion }, nuevoComentario || undefined);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
          <Box sx={{ fontSize: { xs: '0.9rem', sm: '1.0625rem' }, fontWeight: 600, flex: 1, lineHeight: 1.4 }}>
            {(pregunta as any).text}
          </Box>
          {(pregunta as any).required && (
            <Chip label="Obligatoria" size="small" color="error" sx={{ height: { xs: 18, sm: 20 }, fontSize: { xs: '0.625rem', sm: '0.6875rem' }, flexShrink: 0 }} />
          )}
        </Box>
        {(pregunta as any).description && (
          <Box sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, color: 'text.secondary', fontStyle: 'italic', mt: 0.5, lineHeight: 1.3 }}>
            {(pregunta as any).description}
          </Box>
        )}
      </Box>

      {/* Sección de respuesta */}
      <Box 
        sx={{ 
          p: { xs: 1.5, sm: 1.5, md: 2 }, 
          bgcolor: 'action.hover', 
          borderRadius: 1,
          mb: pregunta.permite_comentario ? 2 : 0,
        }}
      >
        <Typography 
          variant="caption" 
          sx={{ 
            display: 'block', 
            mb: 1.5, 
            fontWeight: 600,
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            fontSize: { xs: '0.65rem', sm: '0.75rem' },
          }}
        >
          Selecciona una opción
        </Typography>

        {/* Mobile: ToggleButtons (touch-friendly) */}
        {isMobile && (
          <ToggleButtonGroup
            orientation="vertical"
            value={valorActual?.opcion || null}
            exclusive
            onChange={handleToggleChange}
            fullWidth
            sx={{
              gap: 1,
              '& .MuiToggleButton-root': {
                py: 1.5,
                px: 2,
                fontSize: '0.9375rem',
                fontWeight: 500,
                textAlign: 'left',
                justifyContent: 'flex-start',
                borderWidth: 2,
                borderRadius: 1.5,
                textTransform: 'none',
                lineHeight: 1.3,
                transition: 'all 0.2s ease',
                '&.Mui-selected': {
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  borderWidth: 3,
                  fontWeight: 600,
                  transform: 'scale(1.02)',
                  boxShadow: 1,
                  '&:hover': {
                    bgcolor: 'primary.dark',
                  },
                },
                '&:active': {
                  transform: 'scale(0.98)',
                },
              },
            }}
          >
            {(pregunta as any).options?.map((opcion: any, index: number) => (
              <ToggleButton
                key={`${pregunta.id}-opcion-${index}`}
                value={opcion.label || opcion.texto || opcion.value}
              >
                {opcion.label || opcion.texto || opcion.value}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}

        {/* Desktop: Radio buttons tradicionales */}
        {!isMobile && (
          <RadioGroup value={valorActual?.opcion || ''} onChange={handleChange}>
            {(pregunta as any).options?.map((opcion: any, index: number) => (
              <FormControlLabel
                key={`${pregunta.id}-opcion-${index}`}
                value={opcion.label || opcion.texto || opcion.value}
                control={<Radio />}
                label={opcion.label || opcion.texto || opcion.value}
                sx={{
                  mb: 0.5,
                  '& .MuiFormControlLabel-label': {
                    fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                  },
                }}
              />
            ))}
          </RadioGroup>
        )}
      </Box>

      {/* Comentario */}
      {pregunta.permite_comentario && (
        <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography 
            variant="caption" 
            sx={{ 
              display: 'block', 
              mb: 1, 
              fontWeight: 600,
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: 0.5,
            }}
          >
            Comentario Adicional
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Comentario (opcional)"
            value={comentario}
            onChange={handleComentarioChange}
            placeholder="Agrega un comentario..."
            variant="outlined"
            size="small"
            name={`comentario-${pregunta.id}`}
            id={`comentario-multiple-${pregunta.id}`}
            inputProps={{
              name: `comentario-${pregunta.id}`,
            }}
            InputProps={{
              name: `comentario-${pregunta.id}`,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'background.paper',
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default QuestionMultiple;
